import { Card, LinearProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { useEffect, useRef } from 'react';
import { feedback } from '../services';

interface UseRegisterFormOptions<TData, TCreate, TUpdate> {
  /** Query key prefix for fetching existing data */
  queryKey: string;
  /** Fetch existing data by id (returns undefined when no id) */
  queryFn: (id: number) => Promise<TData | undefined>;
  /** Create mutation function */
  createFn: (data: TCreate) => Promise<any>;
  /** Update mutation function */
  updateFn: (data: TUpdate) => Promise<any>;
  /** The id being edited (undefined = creating new) */
  editId?: number;
  /** Called after successful save */
  onSuccess: () => void;
  /** Success message on create */
  createMessage?: string;
  /** Success message on update */
  updateMessage?: string;
  /** Callback to sync extra state / setData when editing data loads */
  onLoadData?: (data: TData, formRef: React.RefObject<FormHandles>) => void;
}

interface UseRegisterFormReturn<TData, TCreate, TUpdate> {
  formRef: React.RefObject<FormHandles>;
  isEditing: boolean;
  isLoadingData: boolean;
  isSaving: boolean;
  data: TData | undefined;
  /** Direct access to create mutation (for custom submit flows) */
  create: (dto: TCreate) => void;
  /** Direct access to update mutation (for custom submit flows) */
  update: (dto: TUpdate) => void;
  LoadingSkeleton: React.FC;
}

export function useRegisterForm<TData, TCreate, TUpdate>(
  options: UseRegisterFormOptions<TData, TCreate, TUpdate>
): UseRegisterFormReturn<TData, TCreate, TUpdate> {
  const {
    queryKey,
    queryFn,
    createFn,
    updateFn,
    editId,
    onSuccess,
    createMessage = 'Registro criado',
    updateMessage = 'Registro editado',
    onLoadData,
  } = options;

  const queryClient = useQueryClient();
  const formRef = useRef<FormHandles>(null);
  const onLoadDataRef = useRef(onLoadData);
  onLoadDataRef.current = onLoadData;

  const { data, isLoading: isLoadingData } = useQuery({
    queryKey: [queryKey, editId],
    queryFn: () => (editId ? queryFn(editId) : undefined),
    enabled: !!editId,
    staleTime: 30_000,
  });

  const { mutate: create, isLoading: isCreating } = useMutation(
    (dto: TCreate) => createFn(dto),
    {
      onSuccess: () => { feedback(createMessage, 'success'); onSuccess(); },
      onError: (error: Error) => feedback(String(error), 'error'),
    }
  );

  const { mutate: update, isLoading: isUpdating } = useMutation(
    (dto: TUpdate) => updateFn(dto),
    {
      onSuccess: () => {
        feedback(updateMessage, 'success');
        if (editId) {
          queryClient.invalidateQueries([queryKey, editId]);
        }
        onSuccess();
      },
      onError: (error: Error) => feedback(String(error), 'error'),
    }
  );

  useEffect(() => {
    if (data) {
      formRef.current?.setData(data);
      onLoadDataRef.current?.(data, formRef);
    }
  }, [data]);

  const LoadingSkeleton: React.FC = () => (
    <Card sx={{ width: 400, height: 200 }}>
      <LinearProgress />
    </Card>
  );

  return {
    formRef,
    isEditing: !!editId,
    isLoadingData,
    isSaving: isCreating || isUpdating,
    data,
    create,
    update,
    LoadingSkeleton,
  };
}
